import { ref } from "vue";
import axios from "axios";
import { apiPathUrl } from "@/Utils/helpers";
import { toast } from "vue-sonner";

type ProductItem = {
    id: number;
    hash: string;
    collection_order?: number;
};

export function useCollectionSorting(collectionHash: string) {
    const isSaving = ref(false);
    const lastSavedOrder = ref<string>("");
    const pendingUpdate = ref<NodeJS.Timeout | null>(null);

    const saveOrder = async (movedProduct: {
        id: string;
        oldIndex: number;
        newIndex: number;
    }) => {
        // Cancel any pending update
        if (pendingUpdate.value) {
            clearTimeout(pendingUpdate.value);
        }

        // The API expects the new order position (1-based, not 0-based)
        const newOrder = movedProduct.newIndex + 1;

        // Debounce the save
        pendingUpdate.value = setTimeout(async () => {
            isSaving.value = true;

            try {
                await axios.post(
                    apiPathUrl(`/collections/${collectionHash}/order`),
                    {
                        id: movedProduct.id,
                        order: String(newOrder), // API expects string
                    },
                );

                toast.success("Volgorde opgeslagen");
            } catch (error) {
                toast.error("Fout bij opslaan volgorde. Probeer het opnieuw.");
                console.error("Failed to save collection order:", error);
            } finally {
                isSaving.value = false;
            }
        }, 500); // 500ms debounce
    };

    const toggleOrdered = async (currentOrdered: boolean): Promise<boolean> => {
        try {
            const response = await axios.post(
                apiPathUrl(`/collections/${collectionHash}/toggle-ordered`),
            );

            const newOrderedState =
                response.data.ordered ?? response.data.data?.ordered;

            if (newOrderedState) {
                toast.success("Aangepaste volgorde ingeschakeld");
            } else {
                toast.success("Aangepaste volgorde uitgeschakeld");
            }

            return newOrderedState;
        } catch (error) {
            toast.error("Fout bij wijzigen volgorde. Probeer het opnieuw.");
            // console.error("Failed to toggle collection ordered state:", error);
            return currentOrdered; // Return current state on error
        }
    };

    // Cleanup on unmount
    const cleanup = () => {
        if (pendingUpdate.value) {
            clearTimeout(pendingUpdate.value);
        }
    };

    return {
        isSaving,
        saveOrder,
        toggleOrdered,
        cleanup,
    };
}
